<script>
  // # # # # # # # # # # # # #
  //
  //  Feed list
  //
  // # # # # # # # # # # # # #

  // *** IMPORT
  import { intro, session } from "../stores.js";
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";

  // *** COMPONENTS
  import FeedItem from "../Components/FeedItem.svelte";
  import Logo from "../Components/Logo.svelte";
  import Scroller from "../Components/Scroller.svelte";

  // *** PROPS
  export let endpoint = "";

  // *** DOM REFERENCES
  let containerEl = {};
  let sentinel = {};

  // *** VARIABLES
  let items = [];
  let url = "";
  let meta = {};
  let finishedLoading = false;
  let index = 0;
  let firstLoad = false;
  let showScrollText = false;
  let firstScroll = true;

  if (!$session) {
    intro.set(true);
    session.set(true);
  }

  const hideIntro = () => {
    intro.set(false);
    window.scrollTo({
      top: window.innerHeight / 1.5,
      left: 0,
      behavior: "smooth"
    });
  };

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0 && firstLoad) {
          if (meta.nextindex < meta.lastindex) {
            loadData(meta.nextindex);
          } else {
            observer.disconnect();
            finishedLoading = true;
          }
        }
      });
    },
    { treshhold: 0.5 }
  );

  const repositionSentinel = () => {
    if (containerEl && sentinel) {
      const fourthElementFromEnd = containerEl.querySelector(
        ".feed-item:nth-last-child(4)"
      );
      if (fourthElementFromEnd) {
        containerEl.insertBefore(sentinel, fourthElementFromEnd);
      }
    }
  };

  const loadData = i => {
    url = endpoint + "/index:" + i;
    fetch(url)
      .then(r => r.json())
      .then(arr => {
        firstLoad = true;
        items = [...items, ...arr.posts];
        meta = arr.meta;
        setTimeout(repositionSentinel, 500);
      });
  };

  const banner = loadBanner();

  // *** ON MOUNT
  async function loadBanner() {
    const res = await fetch("https://completeworks.ch/about.json");
    const post = await res.json();
    console.dir(post);
    return post;
  }

  loadData(index);

  // *** ON MOUNT
  onMount(async () => {
    window.scrollTo(0, 0);
    observer.observe(sentinel);
    if (intro) {
      window.addEventListener("scroll", e => {
        if (window.scrollY > window.innerHeight / 4) {
          intro.set(false);
        }
        if (window.scrollY > window.innerHeight / 2) {
          showScrollText = true;
          setTimeout(() => {
            showScrollText = false;
            firstScroll = false;
          }, 3000);
        }
        if (window.scrollY > window.innerHeight * 1.5) {
          showScrollText = false;
          firstScroll = false;
        }
      });
    }
  });
</script>

<style lang="scss">
  @import "../_variables.scss";

  .load-trigger {
    width: 100%;
    height: 10px;
    background: transparent;
  }

  .feed {
    user-select: none;
    width: 100vw;
    overflow-x: hidden;
  }

  .scroll-indicator {
    position: fixed;
    z-index: 1100;
    bottom: 40px;
    left: 50%;
    width: 70px;
    height: 70px;
    margin-top: -35px;
    margin-left: -35px;
    opacity: 1;
    transition: opacity $fast-easing, transform $fast-easing;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: scale(1) rotate(-90deg);
    cursor: pointer;

    mix-blend-mode: difference;

    animation-name: pulse;
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    animation-timing-function: $easing;

    svg {
      width: 100%;
      height: 100%;

      path {
        fill: $black;
      }
    }

    &--hidden {
      opacity: 0;
      pointer-events: none;
      transition: opacity $slow-easing;
      animation-name: none;
    }

    @include screen-size("small") {
      display: none;
    }

    &.light {
      svg {
        path {
          fill: $white;
        }
      }
    }

    &:hover {
      animation-name: none;
      transform: scale(1.2) rotate(-90deg);
    }
  }

  @keyframes pulse {
    from {
      transform: scale(1) rotate(-90deg);
    }
    to {
      transform: scale(1.1) rotate(-90deg);
    }
  }

  .big-logo {
    position: fixed;
    z-index: 1000;
    top: 45%;
    left: 50%;
    width: 800px;
    transform: translateX(-50%) translateY(-50%);

    opacity: 1;
    pointer-events: all;
    transition: opacity 0.5s $easing;
    cursor: pointer;

    mix-blend-mode: difference;

    &--hidden {
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.5s $easing;
    }

    @include screen-size("small") {
      display: none;
    }

    &.light {
      svg {
        path {
          fill: $white;
        }
      }
    }
  }

  .scroll-text {
    position: fixed;
    z-index: 1000;
    top: 47%;
    left: 50%;
    width: 800px;
    transform: translateX(-50%) translateY(-50%);
    text-align: center;

    font-family: $sans-stack;
    font-size: 96px;
    color: $black;
    color: white;

    opacity: 1;
    pointer-events: all;
    transition: opacity 0.5s $easing;
    cursor: pointer;

    mix-blend-mode: difference;

    &--hidden {
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.5s $easing;
    }

    @include screen-size("small") {
      display: none;
    }
  }

  .sentinel {
    height: 0px;
    line-height: 0;
  }
</style>

<svelte:head>
  <title>COMPLETE WORKS</title>
</svelte:head>

{#if $intro}
  <div
    class="big-logo"
    on:click={hideIntro}
    out:fly={{ y: -80, duration: 500 }}>
    <Logo white={true} />
  </div>

  <div class="scroll-indicator light" out:fade on:click={hideIntro}>
    <svg viewBox="0 0 62.68 55">
      <path
        d="M5.64,27.05c2.31-2.37,8.66-9,14.19-14.89L30.47.9H26.84c-6,6.35-18.48,20.31-25.49,27.5,7.09,7,19.55,21.16,25.49,27.5h3.63q-5.7-6.21-10.64-11.42C14.38,38.47,8,31.79,5.64,29.59H64V27.05Z" />
    </svg>
  </div>
{/if}

{#if showScrollText && firstScroll}
  <div
    class="scroll-text"
    in:fly={{ y: -40, duration: 400 }}
    out:fly={{ y: 80, duration: 500 }}>
    Scroll with us
  </div>
{/if}

{#await banner then banner}
  {#if !$intro && banner.header.showBanner}
    <Scroller text={banner.header.bannerText} link={banner.header.bannerLink} />
  {/if}
{/await}

<div class="feed">

  <div class="container" bind:this={containerEl}>
    {#each items as item, i}
      <FeedItem {item} />
    {/each}
    {#if !finishedLoading}
      <div class="sentinel" bind:this={sentinel} />
    {/if}

  </div>

</div>
